'use client'
import {
  SidebarContent,
  SidebarGroup,
  useSidebar,
} from '@/components/ui/sidebar'

import { Accordion } from '@/components/ui/accordion'
import { ChipSelect } from '@/components/main/contests/chip-select'

import { EducationalLevelAccordion } from '../filter/sections/educational-level'
import { RegionAccordion } from '../filter/sections/region'
import { MemberAccordion } from '../filter/sections/member'
// import { UniversityAccordion } from '../filter/sections/university'
import { RewardAccordion } from '../filter/sections/reward'
import { SortbySection } from '../filter/sections/sortby'
import {
  getPreferenceData,
  useUpdatePreference,
} from '@/hooks/api/use-preference'
import { getAuthHeader } from '@/services/api/client'
import { LoadingScreen } from '@/components/common/loading'
import { categoryChips } from '@/hooks/api/use-categories'
import { SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PreferenceFormSchema, preferenceFormSchema } from './form'
import { useState } from 'react'
import { cn } from '@/services/utils/cn'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form'
// import { InternationalAccordion } from '../filter/sections/international'

export function AppSidebarContent() {
  const [isLoading, setIsLoading] = useState(true)
  const { updatePreference } = useUpdatePreference()

  const { control, setValue, handleSubmit, formState } =
    useForm<PreferenceFormSchema>({
      resolver: zodResolver(preferenceFormSchema),
      defaultValues: async () => {
        try {
          setIsLoading(true)
          const prefeernceValue = await getPreferenceData()
          setIsLoading(false)

          return {
            categories: prefeernceValue.categories ?? [],
            certificate: prefeernceValue.certificate ?? false,
            companyPartnership: prefeernceValue.company_partnership ?? false,
            educationalLevels: prefeernceValue.educational_level ?? [],
            internship: prefeernceValue.internship ?? false,
            trophy: prefeernceValue.trophy ?? false,
            publishWork: prefeernceValue.publish_work ?? false,
            isViewActiveContests: prefeernceValue.show_only_active ?? false,
            memberTypes: prefeernceValue.member_type ?? [],
            openDate: prefeernceValue.open_date ?? new Date(),
            closingDate: prefeernceValue.closing_date ?? new Date(),
            prizeNumberMax: prefeernceValue.prize_number_max ?? 0,
            prizeNumberMin: prefeernceValue.prize_number_min ?? 0,
            regions: prefeernceValue.region ?? [],
            sortBy: prefeernceValue.sort_by ?? undefined,
            sortOrder: prefeernceValue.sort_order ?? undefined,
            teamNumberMax: prefeernceValue.team_number_max ?? 0,
            teamNumberMin: prefeernceValue.team_number_min ?? 0,
          }
        } catch (error: unknown) {
          setIsLoading(false)
          return {
            categories: [],
            certificate: false,
            companyPartnership: false,
            educationalLevels: [],
            internship: false,
            trophy: false,
            publishWork: false,
            isViewActiveContests: false,
            memberTypes: [],
            openDate: new Date(),
            closingDate: new Date(),
            prizeNumberMax: 0,
            prizeNumberMin: 0,
            regions: [],
            sortBy: undefined,
            sortOrder: undefined,
            teamNumberMax: 0,
            teamNumberMin: 0,
          }
        }
      },
    })

  const { toggleSidebar } = useSidebar()

  const handleSavePreference: SubmitHandler<PreferenceFormSchema> = async (
    values
  ) => {
    updatePreference({
      headers: getAuthHeader(),
      body: {
        category_ids: values.categories,
        certificate: values.certificate,
        company_partnership: values.companyPartnership,
        internship: values.internship,
        educational_level: values.educationalLevels ?? [],
        member_type: values.memberTypes,
        open_date: values.openDate?.toISOString(),
        closing_date: values.closingDate?.toISOString(),
        prize_number_max: values.prizeNumberMax,
        prize_number_min: values.prizeNumberMin,
        publish_work: values.publishWork,
        region: values.regions,
        show_only_active: values.isViewActiveContests,
        sort_by: values.sortBy,
        sort_order: values.sortOrder,
        team_number_max: values.teamNumberMax,
        team_number_min: values.teamNumberMin,
        trophy: values.trophy,
      },
    })
    toggleSidebar()
  }

  const [isPrizeSliderActive, setIsPrizeSliderActiveState] = useState(false)
  const [isBooleanOptionActive, setIsBooleanOptionActive] = useState(false)

  if (isLoading) {
    return (
      <SidebarContent>
        <div className="flex h-full items-center justify-center">
          <LoadingScreen />
        </div>
      </SidebarContent>
    )
  }

  return (
    <SidebarContent>
      <SidebarGroup>
        <form
          className="grid gap-4"
          onSubmit={handleSubmit(handleSavePreference)}
        >
          <div className="grid gap-2">
            <h2 className="font-darkergrotesque text-xl font-bold">
              Interested Categories
            </h2>

            <FormField
              control={control}
              name="categories"
              render={({ field }) => (
                <ChipSelect
                  align="left"
                  initialChips={field?.value?.map((category: number) => {
                    const chip = categoryChips.find(
                      (chip) => chip.id === category.toString()
                    )
                    return {
                      id: category.toString(),
                      chipName: chip?.chipName || 'Unknown',
                      activeColor:
                        chip?.activeColor ||
                        'border border-zinc-900 bg-zinc-900 text-white',
                      inactiveColor:
                        chip?.inactiveColor || 'border border-zinc-900',
                    }
                  })}
                  onFilterChange={(activeChips) => {
                    field?.onChange(activeChips.map((chip) => Number(chip.id)))
                  }}
                />
              )}
            />
          </div>

          <div className="grid gap-2">
            <h2 className="font-darkergrotesque text-xl font-bold">Filters</h2>

            <Accordion type="multiple">
              <EducationalLevelAccordion control={control} />
              <RegionAccordion control={control} />
              <MemberAccordion
                control={control}
                // TODO min max slider as prop
              />
              {/* <UniversityAccordion/> */}
              <RewardAccordion
                control={control}
                setValue={setValue}
                isBooleanOptionActive={isBooleanOptionActive}
                isPrizeSliderActive={isPrizeSliderActive}
                setIsBooleanOptionActive={setIsBooleanOptionActive}
                setIsPrizeSliderActive={setIsPrizeSliderActiveState}
                // TODO min max slider as prop
              />
              {/* <InternationalAccordion /> */}
            </Accordion>

            <SortbySection
              control={control}
              setValue={setValue}
              // TODO add value
            />

            <div className="mt-[25px]">
              <Button
                type="submit"
                variant="outline"
                className={cn(
                  'w-full rounded-full bg-transparent text-sm font-bold text-zinc-950',
                  formState.isDirty &&
                    'bg-zinc-950 text-white hover:bg-zinc-800 hover:text-white',
                  formState.isSubmitting && 'opacity-50'
                )}
              >
                Save preferences
              </Button>
            </div>
          </div>
        </form>
      </SidebarGroup>
    </SidebarContent>
  )
}
